/* eslint-disable max-lines */
import {
  startTransition,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  ColumnDef,
  ColumnFiltersState,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  PaginationState,
  RowSelectionState,
  SortingState,
  useReactTable,
  VisibilityState
} from '@tanstack/react-table';
import cn from 'classnames';
import { ChevronRight } from 'lucide-react';
import { useThemeContext } from '../../../utils/providers/theme-context';
import { useNewAppSearchParams } from '../../../utils/hooks/use-new-app-search-params';
import { useGridActionController } from './grid-action-controller';
import { RowActionsCell } from './row-action-v2';
import Checkbox from '../ui-kit/checkbox';
import { DataGridV2Props } from './data-grid-types';
import {
  buildQFromColumnFilters,
  parseQToColumnFilters
} from './data-grid-utils';

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 640);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);
  return isMobile;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function useDataGridTable<T extends Record<string, any>>(
  props: DataGridV2Props<T>
) {
  const {
    data,
    columns,
    enableRowSelection = false,
    onRowSelectionChange,
    enablePagination = true,
    pageSize = 25,
    pageSizeOptions = [25, 50, 75, 100],
    totalItems,
    manualPagination = false,
    onPaginationChange,
    enableSorting = true,
    manualSorting = false,
    onSortingChange,
    defaultSort = [],
    enableFiltering = true,
    manualFiltering = false,
    onFilteringChange,
    enableExpanding = false,
    renderSubComponent,
    getRowCanExpand,
    renderRowActions,
    getRowId,
    onDelete,
    onMove,
    actions: actionsProp = []
  } = props;

  const { t } = useTranslation();
  const { getColour, theme, getBrandTint } = useThemeContext();
  const isMobile = useIsMobile();

  const themeText = getColour('normalText');
  const themeCellText = getColour('inactiveText');
  const themeLayer = getColour('secondLayer');
  const themeBorder = getColour('hairline');
  const pick = (light: string, dark: string) =>
    theme === 'dark' ? dark : light;

  const headerBg = pick('rgb(249 250 251)', 'rgb(39 39 42)');
  const footerBg = headerBg;
  const copyHoverBg = pick('rgb(244 244 245)', 'rgb(63 63 70)');

  const { searchParams, setSearchParams, setSorting } = useNewAppSearchParams();
  const navigate = useNavigate();

  /* ---------------- State ---------------- */
  const [sorting, setSortingState] = useState<SortingState>(defaultSort);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: manualPagination
      ? Math.floor(
          parseInt(searchParams.offset || '0') /
            parseInt(searchParams.limit || String(pageSize))
        )
      : 0,
    pageSize: manualPagination
      ? parseInt(searchParams.limit || String(pageSize))
      : pageSize
  });

  const containerRef = useRef<HTMLDivElement | null>(null);
  const [isDropdownVisible, setDropdownVisible] = useState(false);
  const [mousePosition, setMousePosition] = useState<{ x: number; y: number }>({
    x: 0,
    y: 0
  });

  const toContainerPos = useCallback((clientX: number, clientY: number) => {
    const el = containerRef.current;
    if (!el) return { x: clientX, y: clientY };
    const rect = el.getBoundingClientRect();
    return {
      x: clientX - rect.left + el.scrollLeft,
      y: clientY - rect.top + el.scrollTop
    };
  }, []);

  const paginationDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const [pendingPageIndex, setPendingPageIndex] = useState<number | null>(null);
  const filterDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const firstPaintRef = useRef(true);

  useEffect(() => {
    firstPaintRef.current = false;
  }, []);

  useEffect(() => {
    if (!manualPagination) return;
    const lim = parseInt(searchParams.limit || String(pageSize), 10);
    const off = parseInt(searchParams.offset || '0', 10);
    const nextIndex = Math.floor(off / lim);
    setPagination(prev => {
      if (prev.pageIndex === nextIndex && prev.pageSize === lim) return prev;
      return { pageIndex: nextIndex, pageSize: lim };
    });
  }, [manualPagination, searchParams.offset, searchParams.limit, pageSize]);

  const loading = props.loading ?? false;
  const isFetching = !!props.isFetching;
  const isServerMode = !!manualPagination;

  // Show overlay the instant the user clicks a page button — before isFetching
  // becomes true (which only happens after the 80ms URL-update debounce).
  const isPaginating = manualPagination && pendingPageIndex !== null;

  // Tracks whether the fetch triggered by pagination has actually started.
  // Without this guard the clearing effect would fire immediately on the same
  // render where pendingPageIndex is set, because isFetching is still false.
  const pendingFetchStartedRef = useRef(false);

  useEffect(() => {
    // Reset tracking whenever a new page is requested.
    if (pendingPageIndex !== null) pendingFetchStartedRef.current = false;
  }, [pendingPageIndex]);

  useEffect(() => {
    if (!manualPagination || pendingPageIndex === null) return;
    if (isFetching) {
      pendingFetchStartedRef.current = true;
      return;
    }
    // isFetching is false — only clear after we've seen it go true first.
    if (pendingFetchStartedRef.current) {
      setPendingPageIndex(null);
    }
  }, [isFetching, manualPagination, pendingPageIndex]);

  // Fallback for callers that don't wire up isFetching (loading-based clear).
  useEffect(() => {
    if (props.isFetching !== undefined) return;
    if (!loading && pendingPageIndex !== null) setPendingPageIndex(null);
  }, [loading, props.isFetching, pendingPageIndex]);

  /* ---------------- Filter commit ---------------- */
  const commitFilterValue = useCallback(
    (columnId: string, next: string, isCommit = false) => {
      const delay = isCommit ? 0 : 300;
      if (filterDebounceRef.current) clearTimeout(filterDebounceRef.current);
      filterDebounceRef.current = setTimeout(() => {
        setColumnFilters(prevFilters => {
          let merged: ColumnFiltersState;

          if (next && next.trim() !== '') {
            const existingIdx = prevFilters.findIndex(f => f.id === columnId);
            if (existingIdx >= 0) {
              merged = [...prevFilters];
              merged[existingIdx] = { id: columnId, value: next.trim() };
            } else {
              merged = [...prevFilters, { id: columnId, value: next.trim() }];
            }
          } else {
            merged = prevFilters.filter(f => f.id !== columnId);
          }

          if (manualFiltering) {
            const qParts = buildQFromColumnFilters(merged, columns);
            const currentLimit =
              (searchParams.limit && String(searchParams.limit)) ||
              String(pagination.pageSize || 25);

            const params = new URLSearchParams();
            params.set('offset', '0');
            params.set('limit', currentLimit);
            Object.entries(searchParams).forEach(([key, value]) => {
              if (key !== 'q' && key !== 'offset' && key !== 'limit' && value) {
                params.set(key, String(value));
              }
            });
            qParts.forEach(qValue => params.append('q', qValue));
            navigate(`${window.location.pathname}?${params.toString()}`, {
              replace: true
            });
            onFilteringChange?.(merged);
          } else {
            onFilteringChange?.(merged);
          }

          return merged;
        });
      }, delay);
    },
    [
      manualFiltering,
      columns,
      searchParams.limit,
      pagination.pageSize,
      setSearchParams,
      onFilteringChange
    ]
  );

  /* ---------------- Actions controller ---------------- */
  const {
    visibleBulkActions,
    execute,
    executeBulk,
    confirmState,
    confirmProceed,
    cancelConfirm
  } = useGridActionController<T>(actionsProp);

  /* ---------------- Hydrate filters from URL ---------------- */
  const lastQRef = useRef<string | undefined>();
  useEffect(() => {
    if (!manualFiltering) return;
    const urlParams = new URLSearchParams(window.location.search);
    const allQ = urlParams.getAll('q');
    const qString = allQ.join(',');
    if (qString === lastQRef.current) return;
    lastQRef.current = qString;
    const next = parseQToColumnFilters(
      allQ.length > 0 ? allQ : undefined,
      columns
    );
    setColumnFilters(next);
    setRowSelection({});
  }, [manualFiltering, searchParams.q, columns]);

  /* ---------------- Hydrate sorting from URL ---------------- */
  useEffect(() => {
    if (!manualSorting) return;
    if (searchParams.sort && searchParams.direction) {
      setSortingState([
        { id: searchParams.sort, desc: searchParams.direction === 'desc' }
      ]);
    } else if (!searchParams.sort && !searchParams.direction) {
      setSortingState([]);
    }
  }, [manualSorting, searchParams.sort, searchParams.direction]);

  /* ---------------- Feature gating ---------------- */
  const hasAnyRowAction = !!(
    renderRowActions ||
    actionsProp.length > 0 ||
    props.setDeletingObject ||
    props.setUpdatingObject ||
    props.setDuplicatingObject ||
    props.setSellingObject ||
    props.setMenuOptions ||
    props.setEditCell ||
    props.iconLinkTo
  );

  /* ---------------- Build columns ---------------- */
  const tableColumns = useMemo<ColumnDef<T>[]>(() => {
    const cols: ColumnDef<T>[] = [];

    if (enableExpanding && renderSubComponent) {
      cols.push({
        id: 'expand',
        header: () => <span className="sr-only">{t('expand-row')}</span>,
        cell: ({ row }) => {
          const canExpand = getRowCanExpand
            ? getRowCanExpand(row.original)
            : true;
          if (!canExpand) return null;
          return (
            <button
              onClick={e => {
                e.stopPropagation();
                setExpanded(prev => ({ ...prev, [row.id]: !prev[row.id] }));
              }}
              className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-2xl transition-colors"
            >
              <ChevronRight
                className={cn(
                  'w-4 h-4 transition-transform duration-200',
                  expanded[row.id] && 'rotate-90'
                )}
              />
            </button>
          );
        },
        size: 40,
        minSize: 40,
        maxSize: 40,
        enableSorting: false
      } as ColumnDef<T>);
    }

    const shouldShowCheckbox =
      enableRowSelection ||
      onDelete ||
      onMove ||
      actionsProp.some(a => !!a.runBulk);

    if (shouldShowCheckbox) {
      cols.push({
        id: 'select',
        header: ({ table }) => {
          const indeterminate =
            table.getIsSomeRowsSelected() && !table.getIsAllRowsSelected();
          return (
            <div className="flex items-center justify-center w-full h-full px-1">
              <Checkbox
                checked={table.getIsAllRowsSelected()}
                onChange={table.getToggleAllRowsSelectedHandler()}
                ref={el => {
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  if (el) (el as any).indeterminate = indeterminate;
                }}
                wrapperClass="!p-0 !m-0"
                className="!size-3.5"
                aria-label={t('select-all')}
              />
            </div>
          );
        },
        cell: ({ row }) => {
          const indeterminate = row.getIsSomeSelected() && !row.getIsSelected();
          return (
            <div className="flex items-center justify-center w-full h-full px-1">
              <Checkbox
                checked={row.getIsSelected()}
                onChange={row.getToggleSelectedHandler()}
                ref={el => {
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  if (el) (el as any).indeterminate = indeterminate;
                }}
                wrapperClass="!p-0 !m-0"
                className="!size-3.5"
                aria-label={t('select-row')}
              />
            </div>
          );
        },
        size: 40,
        minSize: 40,
        maxSize: 40,
        enableSorting: false,
        enableHiding: false
      } as ColumnDef<T>);
    }

    const normalizedColumns = columns.map(col => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const n: any = { ...col };
      if (col.field && !col.accessorKey) n.accessorKey = col.field as string;
      if (n.header === undefined || n.header === null || n.header === '') {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const headerName = (col as any).headerName;
        n.header =
          headerName ??
          (typeof n.accessorKey === 'string'
            ? n.accessorKey
            : String(n.accessorKey ?? n.id ?? ''));
      }
      n.enableColumnFilter = enableFiltering
        ? col.enableFiltering !== false
        : false;
      return n;
    });

    cols.push(...(normalizedColumns as ColumnDef<T>[]));

    if (hasAnyRowAction) {
      if (renderRowActions) {
        cols.push({
          id: 'actions',
          header: () => (
            <div className="flex justify-center">
              <span className="truncate">{t('actions')}</span>
            </div>
          ),
          cell: ({ row }) => renderRowActions(row.original),
          size: 90,
          minSize: 90,
          maxSize: 120,
          enableSorting: false
        } as ColumnDef<T>);
      } else if (actionsProp.length > 0) {
        cols.push({
          id: 'actions',
          header: () => (
            <div className="flex justify-center">
              <span className="truncate">{t('actions')}</span>
            </div>
          ),
          cell: ({ row }) => (
            <div
              onClick={e => e.stopPropagation()}
              onMouseDown={e => e.stopPropagation()}
              onPointerDown={e => e.stopPropagation()}
            >
              <RowActionsCell<T>
                row={row.original}
                actions={actionsProp}
                onExecute={(action, r) => execute(action, r)}
              />
            </div>
          ),
          size: 90,
          minSize: 90,
          maxSize: 120,
          enableSorting: false
        } as ColumnDef<T>);
      }
    }

    return cols;
  }, [
    columns,
    enableRowSelection,
    enableExpanding,
    renderSubComponent,
    getRowCanExpand,
    expanded,
    hasAnyRowAction,
    renderRowActions,
    actionsProp,
    execute,
    t,
    onDelete,
    onMove
  ]);

  /* ---------------- Handlers ---------------- */
  const handleSortingChange = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (updater: any) => {
      const newSorting =
        typeof updater === 'function' ? updater(sorting) : updater;
      setSortingState(newSorting);
      if (manualSorting) {
        if (newSorting.length > 0) {
          setSorting(newSorting[0].id, newSorting[0].desc ? 'desc' : 'asc');
        } else {
          setSorting(undefined, undefined);
        }
        onSortingChange?.(newSorting);
      }
    },
    [sorting, manualSorting, onSortingChange, setSorting]
  );

  const handleFilteringChange = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (updater: any) => {
      const newFilters =
        typeof updater === 'function' ? updater(columnFilters) : updater;
      setColumnFilters(newFilters);
      if (!manualFiltering) onFilteringChange?.(newFilters);
    },
    [columnFilters, manualFiltering, onFilteringChange]
  );

  const handlePaginationChange = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (updater: any) => {
      const next =
        typeof updater === 'function' ? updater(pagination) : updater;
      startTransition(() => setPagination(next));
      if (!manualPagination) return;
      setPendingPageIndex(next.pageIndex);
      if (paginationDebounceRef.current)
        clearTimeout(paginationDebounceRef.current);
      paginationDebounceRef.current = setTimeout(() => {
        const offset = next.pageIndex * next.pageSize;
        setSearchParams({
          offset: String(offset),
          limit: String(next.pageSize),
          q: searchParams?.q ?? undefined
        });
        onPaginationChange?.(next);
      }, 80);
    },
    [
      pagination,
      manualPagination,
      onPaginationChange,
      setSearchParams,
      searchParams?.q
    ]
  );

  const handleRowSelectionChange = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (updater: any) => {
      const newSelection =
        typeof updater === 'function' ? updater(rowSelection) : updater;
      setRowSelection(newSelection);
      if (onRowSelectionChange) {
        const idToRow = new Map<string, T>();
        data.forEach((row, idx) => {
          const id =
            typeof getRowId === 'function'
              ? String(getRowId(row, idx))
              : String(idx);
          idToRow.set(id, row);
        });
        const selected = Object.entries(newSelection)
          .filter(([, isSelected]) => isSelected)
          .map(([id]) => idToRow.get(String(id)))
          .filter(Boolean) as T[];
        onRowSelectionChange(selected);
      }
    },
    [rowSelection, onRowSelectionChange, data, getRowId]
  );

  /* ---------------- Table instance ---------------- */
  const table = useReactTable({
    data,
    columns: tableColumns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel:
      enableSorting && !manualSorting ? getSortedRowModel() : undefined,
    getFilteredRowModel:
      enableFiltering && !manualFiltering ? getFilteredRowModel() : undefined,
    getPaginationRowModel:
      enablePagination && !manualPagination
        ? getPaginationRowModel()
        : undefined,
    onSortingChange: handleSortingChange,
    onColumnFiltersChange: handleFilteringChange,
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: handleRowSelectionChange,
    onPaginationChange: handlePaginationChange,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      pagination
    },
    enableRowSelection,
    manualPagination,
    manualSorting,
    manualFiltering,
    pageCount:
      manualPagination && totalItems
        ? Math.ceil(totalItems / pagination.pageSize)
        : undefined,
    getRowId: getRowId ? (row, index) => getRowId(row, index) : undefined
  });

  /* ---------------- Derived ---------------- */
  const rowCount = table.getRowModel().rows.length;
  const totalKnown = typeof totalItems === 'number';

  const shouldShowLoading =
    (!isServerMode && firstPaintRef.current && rowCount === 0 && loading) ||
    (isServerMode && !totalKnown && rowCount === 0);

  const shouldShowNoData =
    !shouldShowLoading &&
    rowCount === 0 &&
    (!isServerMode || (totalKnown && totalItems === 0));

  const currentPage = pagination.pageIndex + 1;
  const totalPages =
    manualPagination && totalItems
      ? Math.ceil(totalItems / pagination.pageSize)
      : table.getPageCount();
  const canPreviousPage = table.getCanPreviousPage();
  const canNextPage = table.getCanNextPage();

  const getPageNumbers = () => {
    const pages: (number | 'gap')[] = [];
    const maxVisible = 7;
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (currentPage <= 3) {
        for (let i = 2; i <= 4; i++) pages.push(i);
        pages.push('gap', totalPages - 1, totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push('gap');
        for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(
          'gap',
          currentPage - 1,
          currentPage,
          currentPage + 1,
          'gap',
          totalPages
        );
      }
    }
    return pages;
  };

  const selectedRowsArray: T[] = table
    .getSelectedRowModel()
    .flatRows.map(r => r.original);
  const isAllOnPage = table.getIsAllRowsSelected();
  Object.assign(selectedRowsArray, { selectAll: isAllOnPage });

  return {
    /* theme */
    themeText,
    themeCellText,
    themeLayer,
    themeBorder,
    headerBg,
    footerBg,
    copyHoverBg,
    getColour,
    getBrandTint,
    theme,
    pick,
    /* state */
    isMobile,
    sorting,
    columnFilters,
    setColumnFilters,
    rowSelection,
    setRowSelection,
    pagination,
    setPagination,
    expanded,
    setExpanded,
    /* context menu */
    containerRef,
    mousePosition,
    setMousePosition,
    isDropdownVisible,
    setDropdownVisible,
    toContainerPos,
    /* table */
    table,
    tableColumns,
    /* actions */
    visibleBulkActions,
    execute,
    executeBulk,
    confirmState,
    confirmProceed,
    cancelConfirm,
    /* filter */
    commitFilterValue,
    /* url */
    searchParams,
    setSearchParams,
    navigate,
    /* derived */
    isServerMode,
    isPaginating,
    shouldShowLoading,
    shouldShowNoData,
    currentPage,
    totalPages,
    canPreviousPage,
    canNextPage,
    getPageNumbers,
    selectedRowsArray,
    pageSizeOptions,
    /* i18n */
    t
  };
}
